/**
 * Commercial tab: what this client pays and how, set per client because prices vary by deal and
 * city. The terms are zData details in the account's "Commercial" area, which zInspector's
 * remote signature uses as placeholders in the agreement template, so the contract carries
 * exactly what is set here. The agreement itself is the engagement (lease) signature.
 *
 * zInspector's public API can't write zData yet: on the live API, "Preview the request" shows
 * what zCRM would send; the demo's fake zInspector saves it.
 */
import {
  Form,
  useDataProvider,
  useNotify,
  useRefresh,
  useTranslate,
} from "ra-core";
import { useState, type ReactNode } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import type { ZcrmAccount } from "../providers/zinspector/accounts";
import { isFakeZinspector } from "../providers/zinspector/client";
import {
  buildCommercialTermsRequest,
  summarizeCommercialTerms,
  validateCommercialTerms,
  type CommercialTerms,
} from "../providers/zinspector/commercialTerms";
import {
  INVOICING_OPTIONS,
  PRICING_MODELS,
} from "../providers/zinspector/crmConfig";
import type { ZcrmDataProvider } from "../providers/zinspector/dataProvider";
import type { ZiWriteRequest } from "../providers/zinspector/newClient";
import { EmptyValue, formatDoors } from "./accountDisplay";
import { RequestPreview } from "./RequestPreview";

const toChoices = (values: readonly string[]) =>
  values.map((value) => ({ id: value, name: value }));

export const CommercialTab = ({ account }: { account: ZcrmAccount }) => {
  const [preview, setPreview] = useState<ZiWriteRequest | null>(null);
  const summary = summarizeCommercialTerms(account.commercial);

  return (
    <div className="flex flex-col gap-4">
      <Form
        record={{ id: account.id, ...account.commercial }}
        validate={validateCommercialTerms}
      >
        <div className="flex flex-col gap-4">
          <TermsCard
            title="Pricing"
            aside={
              summary ? (
                <Badge variant="success">{summary}</Badge>
              ) : (
                <Badge variant="neutral">not set</Badge>
              )
            }
          >
            <p className="text-sm text-muted-foreground">
              What this client pays. Prices vary by deal and city:{" "}
              {account.market || "no market"},{" "}
              {formatDoors(account) || "doors unknown"} doors.
            </p>
            <PricingInputs />
          </TermsCard>
          <TermsCard title="Payment terms">
            <div className="grid sm:grid-cols-2 gap-4">
              <TermInput source="payment_days" inputMode="numeric" />
              <SelectInput
                source="invoicing"
                label="resources.companies.fields.commercial.invoicing"
                choices={toChoices(INVOICING_OPTIONS)}
                emptyText="Not set"
              />
              <TermInput source="terms_effective" type="date" />
            </div>
            <TermInput source="terms_notes" multiline />
          </TermsCard>
          <TermsToolbar account={account} onPreview={setPreview} />
        </div>
      </Form>
      {preview ? (
        <RequestPreview
          requests={[preview]}
          note="zInspector's public API can't write zData yet: this is the route zCRM proposes. Until then, enter these details in the account's Commercial area in the zInspector dashboard."
        />
      ) : null}
      <AgreementCard account={account} />
    </div>
  );
};

const PricingInputs = () => {
  const model = useWatch({ name: "pricing_model" });
  return (
    <>
      <SelectInput
        source="pricing_model"
        label="resources.companies.fields.commercial.pricing_model"
        choices={toChoices(Object.values(PRICING_MODELS))}
        emptyText="Not set"
      />
      {model === PRICING_MODELS.plan ? (
        <div className="grid sm:grid-cols-2 gap-4">
          <TermInput source="plan_name" />
          <TermInput source="plan_monthly_fee" inputMode="decimal" />
          <TermInput source="plan_included" inputMode="numeric" />
          <TermInput source="plan_extra_price" inputMode="decimal" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          <TermInput source="price_mimo" inputMode="decimal" />
          <TermInput source="price_periodic" inputMode="decimal" />
        </div>
      )}
    </>
  );
};

const TermInput = ({
  source,
  ...rest
}: {
  source: keyof CommercialTerms;
  type?: string;
  inputMode?: "decimal" | "numeric";
  multiline?: boolean;
}) => (
  <TextInput
    source={source}
    label={`resources.companies.fields.commercial.${source}`}
    {...rest}
  />
);

const TermsCard = ({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) => (
  <Card>
    <CardContent className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-sm font-semibold">{title}</h2>
        {aside}
      </div>
      {children}
    </CardContent>
  </Card>
);

const TermsToolbar = ({
  account,
  onPreview,
}: {
  account: ZcrmAccount;
  onPreview: (request: ZiWriteRequest | null) => void;
}) => {
  const { handleSubmit, reset } = useFormContext();
  const dataProvider = useDataProvider<ZcrmDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [saving, setSaving] = useState(false);
  const canSave = isFakeZinspector();

  const changedRequest = (values: Record<string, unknown>) =>
    buildCommercialTermsRequest(account.id, values, account.commercial);

  const preview = handleSubmit((values) => {
    const request = changedRequest(values);
    if (!request) notify("Nothing changed", { type: "info" });
    onPreview(request);
  });

  const save = handleSubmit(async (values) => {
    if (!changedRequest(values)) {
      notify("Nothing changed", { type: "info" });
      return;
    }
    setSaving(true);
    try {
      await dataProvider.saveCommercialTerms(
        account.id,
        values as Partial<CommercialTerms>,
        account.commercial,
      );
      notify("Commercial terms saved", { type: "success" });
      reset(values);
      refresh();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Saving failed", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  });

  return (
    <div role="toolbar" className="flex justify-end gap-2">
      <Button type="button" variant="outline" onClick={preview}>
        Preview the zInspector request
      </Button>
      {canSave ? (
        <Button type="button" onClick={save} disabled={saving}>
          Save terms
        </Button>
      ) : null}
    </div>
  );
};

/** The agreement is the engagement's signature, sent with zInspector's remote signature. */
const AgreementCard = ({ account }: { account: ZcrmAccount }) => {
  const translate = useTranslate();
  const state = account.agreement_state
    ? translate(`zi.leaseSigStatus.${account.agreement_state}`, {
        _: account.agreement_state,
      })
    : "";
  const rows: [string, ReactNode][] = [
    ["Signer", account.main_contact_name || <EmptyValue />],
    ["Effective from", account.signed_on?.slice(0, 10) || <EmptyValue />],
  ];
  return (
    <TermsCard
      title="Agreement"
      aside={
        <Badge
          variant={
            account.agreement_state === "Complete" ? "success" : "warning"
          }
        >
          {state || "No engagement yet"}
        </Badge>
      }
    >
      <dl className="grid sm:grid-cols-2 gap-4">
        {rows.map(([label, value]) => (
          <div key={label} className="flex flex-col gap-0.5">
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className="text-sm">{value}</dd>
          </div>
        ))}
      </dl>
      <p className="text-sm text-muted-foreground">
        Sent from zInspector's remote signature, with the terms above filling
        the template's placeholders. The signer is the engagement's main
        contact. Sending from zCRM ("Review &amp; send") comes once the API
        exposes it.
      </p>
    </TermsCard>
  );
};
